const AM35_PROFILE_MIN = 0;
const AM35_PROFILE_MAX = 2;

function clampAM35CurrentProfile(profileValue, fallback = AM35_PROFILE_MIN) {
  const numericProfile = Number(profileValue);
  if (!Number.isFinite(numericProfile)) {
    return fallback;
  }

  if (numericProfile < AM35_PROFILE_MIN) {
    return AM35_PROFILE_MIN;
  }
  if (numericProfile > AM35_PROFILE_MAX) {
    return AM35_PROFILE_MAX;
  }

  return numericProfile;
}

function normalizeLegacyAM35CurrentProfile(
  profileValue,
  fallback = AM35_PROFILE_MIN
) {
  const numericProfile = Number(profileValue);
  if (!Number.isFinite(numericProfile)) {
    return fallback;
  }

  // 设备侧 CurrentProfile 现有回包是 1~3，这里统一归一化到前端使用的 0~2。
  if (numericProfile >= 1 && numericProfile <= AM35_PROFILE_MAX + 1) {
    return clampAM35CurrentProfile(numericProfile - 1, fallback);
  }

  return clampAM35CurrentProfile(numericProfile, fallback);
}

function isAM35MacroCoordinateEvent(item) {
  return (
    item &&
    typeof item === "object" &&
    Object.prototype.hasOwnProperty.call(item, "x") &&
    Object.prototype.hasOwnProperty.call(item, "y")
  );
}

const AM35_CLICK_MACRO_DURATION = 1;
const AM35_CLICK_MACRO_MIN_INTERVAL = 1;
const AM35_CLICK_MACRO_MAX_INTERVAL = 60000;
const AM35_CLICK_MACRO_DEFAULT_INTERVAL = 30;
const AM35_CLICK_MACRO_MIN_REPEAT = 1;
const AM35_CLICK_MACRO_MAX_REPEAT = 65535;
const AM35_CLICK_MACRO_DEFAULT_REPEAT = 3;
const AM35_CLICK_MACRO_TAIL_DELAY = 50;
const AM35_CLICK_MACRO_DEFAULT_KEY = "0xf0";
const AM35_MACRO_LOOP_INTERVAL_DEFAULT = 50;
const AM35_MACRO_PRESS_ONLY_DURATION = 0xea60;

function normalizeAM35MacroInterval(
  value,
  fallback = AM35_CLICK_MACRO_DEFAULT_INTERVAL
) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return Math.min(
    AM35_CLICK_MACRO_MAX_INTERVAL,
    Math.max(AM35_CLICK_MACRO_MIN_INTERVAL, Math.floor(numericValue))
  );
}

function normalizeAM35MacroRepeatCount(
  value,
  fallback = AM35_CLICK_MACRO_DEFAULT_REPEAT
) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return Math.min(
    AM35_CLICK_MACRO_MAX_REPEAT,
    Math.max(AM35_CLICK_MACRO_MIN_REPEAT, Math.floor(numericValue))
  );
}

function normalizeAM35BindingRepeatCount(value, fallback = 1) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return Math.min(65535, Math.max(1, Math.floor(numericValue)));
}

function normalizeAM35MacroLoopIntervalValue(
  value,
  fallback = AM35_MACRO_LOOP_INTERVAL_DEFAULT
) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return Math.min(65535, Math.max(1, Math.floor(numericValue)));
}

function normalizeAM35ClickMacroKey(
  value,
  fallback = AM35_CLICK_MACRO_DEFAULT_KEY
) {
  return typeof value === "string" && value.startsWith("0x") ? value : fallback;
}

function buildAM35ClickMacroUiValue({
  click_key = AM35_CLICK_MACRO_DEFAULT_KEY,
  click_interval = AM35_CLICK_MACRO_DEFAULT_INTERVAL,
  click_repeat_times = AM35_CLICK_MACRO_DEFAULT_REPEAT,
} = {}) {
  const normalizedKey = normalizeAM35ClickMacroKey(click_key);
  const normalizedInterval = normalizeAM35MacroInterval(click_interval);
  const normalizedRepeatTimes = normalizeAM35MacroRepeatCount(
    click_repeat_times
  );
  const downKey = normalizedKey.replace("0x", "0x11");
  const upKey = normalizedKey.replace("0x", "0x10");
  const keyOrder = [];
  const pressTime = [];

  for (let index = 0; index < normalizedRepeatTimes; index++) {
    keyOrder.push(downKey);
    pressTime.push(AM35_CLICK_MACRO_DURATION);
    keyOrder.push(upKey);
    pressTime.push(
      index === normalizedRepeatTimes - 1
        ? AM35_CLICK_MACRO_TAIL_DELAY
        : normalizedInterval
    );
  }

  return {
    keyOrder,
    pressTime,
  };
}

function getAM35MacroLoopIntervalValue(macro = {}) {
  if (macro?.setAM35MacroLoopInterval?.interval != null) {
    return normalizeAM35MacroLoopIntervalValue(
      macro.setAM35MacroLoopInterval.interval
    );
  }
  return AM35_MACRO_LOOP_INTERVAL_DEFAULT;
}

function buildAM35MacroLoopIntervalPayload(
  loopInterval,
  fallbackInterval = AM35_MACRO_LOOP_INTERVAL_DEFAULT
) {
  return {
    interval: normalizeAM35MacroLoopIntervalValue(
      loopInterval?.interval,
      fallbackInterval
    ),
  };
}

function inferAM35ClickMacroFromDevicePayload(keyOrder = []) {
  if (!Array.isArray(keyOrder) || keyOrder.length < 3 || keyOrder.length % 3 !== 0) {
    return null;
  }

  const repeatTimes = keyOrder.length / 3;
  const firstKey = keyOrder[0];
  const firstRelativeStart = parseInt(keyOrder[1], 16);
  const firstDuration = parseInt(keyOrder[2], 16);

  if (
    !firstKey ||
    firstKey === "0xff" ||
    !Number.isFinite(firstRelativeStart) ||
    firstRelativeStart !== 0 ||
    !Number.isFinite(firstDuration) ||
    firstDuration !== AM35_CLICK_MACRO_DURATION
  ) {
    return null;
  }

  let clickInterval = null;

  for (let index = 0; index < keyOrder.length; index += 3) {
    const key = keyOrder[index];
    const relativeStart = parseInt(keyOrder[index + 1], 16);
    const duration = parseInt(keyOrder[index + 2], 16);

    if (
      key !== firstKey ||
      key === "0xff" ||
      !Number.isFinite(relativeStart) ||
      !Number.isFinite(duration) ||
      duration !== AM35_CLICK_MACRO_DURATION
    ) {
      return null;
    }

    if (index === 0) {
      continue;
    }

    if (clickInterval === null) {
      clickInterval = relativeStart - AM35_CLICK_MACRO_DURATION;
    }

    if (
      relativeStart !== clickInterval + AM35_CLICK_MACRO_DURATION ||
      clickInterval < AM35_CLICK_MACRO_MIN_INTERVAL
    ) {
      return null;
    }
  }

  return {
    macro_type: "click",
    click_key: firstKey,
    click_interval: normalizeAM35MacroInterval(clickInterval),
    click_repeat_times: normalizeAM35MacroRepeatCount(repeatTimes),
  };
}

function normalizeAM35MacroEntry(macro = {}, fallbackIndex = 0) {
  const baseEntry = {
    index:
      macro.index !== undefined && macro.index !== null ? macro.index : fallbackIndex,
    macro_name: macro.macro_name,
    keyID: macro.keyID,
    toKeyID: macro.toKeyID,
    repeat_mode: macro.repeat_mode,
    repeat_times: macro.repeat_times,
    setAM35MacroLoopInterval: buildAM35MacroLoopIntervalPayload(
      macro.setAM35MacroLoopInterval,
      getAM35MacroLoopIntervalValue(macro)
    ),
  };

  if (macro.macro_type === "click") {
    const repeatMode = macro.repeat_mode === 2 ? 2 : macro.repeat_mode === 1 ? 1 : 0;
    const clickMacro = {
      ...baseEntry,
      macro_type: "click",
      repeat_mode: repeatMode,
      repeat_times:
        repeatMode === 0
          ? normalizeAM35BindingRepeatCount(macro.repeat_times, 1)
          : 0,
      click_key: normalizeAM35ClickMacroKey(macro.click_key),
      click_interval: normalizeAM35MacroInterval(macro.click_interval),
      click_repeat_times: normalizeAM35MacroRepeatCount(
        macro.click_repeat_times
      ),
    };
    clickMacro.macroValue = buildAM35ClickMacroUiValue(clickMacro);
    if (clickMacro.macroValue.pressTime.length > 0) {
      clickMacro.macroValue.pressTime[
        clickMacro.macroValue.pressTime.length - 1
      ] = clickMacro.setAM35MacroLoopInterval.interval;
    }
    return clickMacro;
  }

  return {
    ...baseEntry,
    macro_type: "normal",
    macroValue: {
      keyOrder: Array.isArray(macro.macroValue?.keyOrder)
        ? macro.macroValue.keyOrder
        : [],
      pressTime: Array.isArray(macro.macroValue?.pressTime)
        ? macro.macroValue.pressTime
        : [],
    },
  };
}

const state = {
  // 基本版本信息
  version: "",
  isLatestVersion: true,
  profile: 0,
  
  // 键盘矩阵数据
  keyMatrix: {
    "0x01": [],
    "0x02": [],
    "0x03": [],
    "0x04": [],
    "0x05": [],
    "0x06": [],
    "0x07": [],
  },
  
  // 鼠标参数统一到mouseArgs对象
  mouseArgs: {
    reportRate: 8000,
    currentID: 0, // 0~5
    currentProfile: 0, // 当前配置 0~2
    DPIsyn: 1,
    xDPI: [800, 1600, 2400, 3200, 5000, 10000, 20000, 26000], // （50~26000）
    yDPI: [800, 1600, 2400, 3200, 5000, 10000, 20000, 26000], // （50~26000）
    DPIcolor: [
      "#FF0000",
      "#00FF00",
      "#0000FF",
      "#00FFFF",
      "#800080",
      "#FFFF00",
      "#FF00FF",
      "#ffffff",
    ], // 000000~ffffff
    DPIStages: undefined, // DPI档位数量 (1~8),undefined表示使用所有8档
    getDPIButton: 1, // DPI快速切换开关:1:开启,0:关闭
    wireDebounce: 255,
    rf24gDebounce: 255,
    btDebounce: 255,
    MotionSync: 1,
    LOD: 1,    // 低：0   中：1    高：2
    AngleSnapping: 1, // 直线修正 关：0， 开：1
    RippleCtrl: 1, // 波纹修正 关：0， 开：1
    CurrentProfile: "1",
    AM35FPStatus: 0, // 猛攻模式开关：开：1， 关：0
    AM35rotateAngleSwitch: 0, // 旋转角度开关：0 关 1 开
    setAM35rotateAngle: 0, // 旋转角度：[-90, 90]
    AM35ProfileKeySwitch: null, // 组合键切换 Profile 开关：1 开，0 关，null=尚未收到独立上报
    // 休眠时间
    sleepTime24G: 30, // 2.4G一次休眠 0~65535
    sleepTimeBT: 600, // 蓝牙一次休眠 0~65535
    mouseID: 19351, // 1935 的DPI上限26000，其它的DPI上限30000
  },

  // 灯光数据
  lightData: {
    mouseLightSwitch: "1",   // 灯效开关：1：开0：关
    mouseLightType: "Solid",    //鼠标："Solid", "Breathing", "Neon"
    mouseLightSpeed: "6",    //灯速：[1,10]
    mouseLightRGB: ["00FF00"],      // RGB：静态灯：1个，动态灯：[2,7]
    dongleLightSwitch: "1",   // 灯效开关：1：开0：关
    dongleLightType: 0,      //dongle: 0常亮 1呼吸 2彩虹效果
    dongleColor: "#1400FF", //颜色使用十六进制格式
    dongleSaturationPer: 96, //饱和度：0-100
    dongleColor2: "#1400FF", //颜色需要范围值内
    dongleSaturationPer2: 96, //第二个灯的饱和度：0-100
    dongleColor3: "#1400FF", //第三个灯的颜色
    dongleSaturationPer3: 96, //第三个灯的饱和度：0-100
    dongleSpeed: 20,         //速度：0-100
    dongleBrightness: 240,     //亮度：0-255
  },
  
  // 宏数据
  macroData: [],
  mouseMacroData: [],

  // 向后兼容的属性，引用mouseArgs中的值
  get reportRate() { return this.mouseArgs.reportRate; },
  get currentID() { return this.mouseArgs.currentID; },
  get currentProfile() { return this.mouseArgs.currentProfile; },
  get DPIsyn() { return this.mouseArgs.DPIsyn; },
  get xDPI() { return this.mouseArgs.xDPI; },
  get yDPI() { return this.mouseArgs.yDPI; },
  get DPIcolor() { return this.mouseArgs.DPIcolor; },
  get DPIStages() { return this.mouseArgs.DPIStages; },
  get getDPIButton() { return this.mouseArgs.getDPIButton; },
  // 分别暴露三路去抖
  get wireDebounce() {
    return this.mouseArgs.wireDebounce != null ? this.mouseArgs.wireDebounce : 255;
  },
  get rf24gDebounce() {
    return this.mouseArgs.rf24gDebounce != null ? this.mouseArgs.rf24gDebounce : 255;
  },
  get btDebounce() {
    return this.mouseArgs.btDebounce != null ? this.mouseArgs.btDebounce : 255;
  },
  get MotionSync() { return this.mouseArgs.MotionSync; },
  get LOD() { return this.mouseArgs.LOD; },
  get AngleSnapping() { return this.mouseArgs.AngleSnapping; },
  get RippleCtrl() { return this.mouseArgs.RippleCtrl; },
  get AM35FPStatus() { return this.mouseArgs.AM35FPStatus; },
  get AM35rotateAngleSwitch() { return this.mouseArgs.AM35rotateAngleSwitch; },
  get setAM35rotateAngle() { return this.mouseArgs.setAM35rotateAngle; },
  get AM35ProfileKeySwitch() { return this.mouseArgs.AM35ProfileKeySwitch; },
  get sleepTime24G() { return this.mouseArgs.sleepTime24G; },
  get sleepTimeBT() { return this.mouseArgs.sleepTimeBT; },
  get sleepOneTime24G() { return this.mouseArgs.sleepTime24G; }, // 兼容旧命名
  get sleepOneTimeBT() { return this.mouseArgs.sleepTimeBT; }, // 兼容旧命名
  get mouseID() { return this.mouseArgs.mouseID; },
  
  // 兼容旧代码
  param: {
    get mouseID() { return state.mouseArgs.mouseID; },
    set mouseID(value) { state.mouseArgs.mouseID = value; }
  }
};

const actions = {
  setDPI({ commit }, DPI) {
    commit("SET_DPI", DPI);
  },
  setDebounce({ commit }, Debounce) {
    commit("SET_DEBOUNCE", Debounce);
  },
  setReportRate({ commit }, reportRate) {
    commit("SET_REPORT_RATE", reportRate);
  },
  setLOD({ commit }, LOD) {
    commit("SET_LOD", LOD);
  },
  setMotionSync({ commit }, MotionSync) {
    commit("SET_MOTION_SYNC", MotionSync);
  },
  setAngleSnapping({ commit }, AngleSnapping) {
    commit("SET_ANGLE_SNAPPING", AngleSnapping);
  },
  setRippleCtrl({ commit }, RippleCtrl) {
    commit("SET_RIPPLE_CTRL", RippleCtrl);
  },
  setAM35FPStatus({ commit }, AM35FPStatus) {
    commit("SET_AM35FP_STATUS", AM35FPStatus);
  },
  setAM35RotateAngle({ commit }, rotateAngle) {
    commit("SET_AM35_ROTATE_ANGLE", rotateAngle);
  },
  setAM35ProfileKeySwitch({ commit }, profileKeySwitch) {
    commit("SET_AM35_PROFILE_KEY_SWITCH", profileKeySwitch);
  },
  // 新增方法用于设置整个mouseArgs对象
  setMouseArgs({ commit }, mouseArgs) {
    commit("SET_MOUSE_ARGS", mouseArgs);
  },
  setSleepTime24G({ commit }, sleepTime24G) {
    commit("SET_SLEEP_TIME_24G", sleepTime24G);
  },
  setSleepTimeBT({ commit }, sleepTimeBT) {
    commit("SET_SLEEP_TIME_BT", sleepTimeBT);
  },
  // 保持旧方法以向后兼容
  setSleepOneTime24G({ commit }, sleepOneTime24G) {
    commit("SET_SLEEP_TIME_24G", sleepOneTime24G);
  },
  setSleepOneTimeBT({ commit }, sleepOneTimeBT) {
    commit("SET_SLEEP_TIME_BT", sleepOneTimeBT);
  },

  setVersion({ commit }, { version, isLatestVersion }) {
    commit("SET_VERSION", { version, isLatestVersion });
  },
  setProfile({ commit }, profile) {
    commit("SET_PROFILE", profile);
  },
  setKeyMatrix({ commit }, keyMatrix) {
    commit("SET_KEY_MATRIX", keyMatrix);
  },

  /* UI数据 → 设备数据转换
     输入格式 (UI):
       keyOrder: ["0x1104", "0x1105", "0x1004", "0x1005"]
       pressTime: [30, 40, 50] 或 [30, 40, 50, 50]

     UI 数据含义:
       - keyOrder[i] 是第 i 个事件（按下 / 松开 / 坐标）
       - pressTime[i] 是“事件 i → 事件 i+1”的时间间隔
       - 所以前 keyOrder.length - 1 项才是真正参与时间线计算的间隔
       - 当前 AM35 UI / 回填流程会额外保留一个末尾 50 哨兵位
         所以运行时经常看到 pressTime.length === keyOrder.length
         但协议语义上也要兼容 pressTime.length === keyOrder.length - 1

     旧串行流程保持不变:
       keyOrder: ["0x1104", "0x1004", "0x1105", "0x1005", "0x1106", "0x1006"]
       pressTime: [50, 50, 50, 50, 50]

     第1步：先还原旧串行流程的绝对时间线
       - eventStartTimes[0] = 0      → A按下
       - eventStartTimes[1] = 50     → A抬起
       - eventStartTimes[2] = 100    → B按下
       - eventStartTimes[3] = 150    → B抬起
       - eventStartTimes[4] = 200    → C按下
       - eventStartTimes[5] = 250    → C抬起

     第2步：压成设备三元组后，仍然是旧协议格式
       - A按下 → ["0x04", "0x000000", "0x000032"]
         start=0，因为它是第一个输出事件
         duration=50，因为 A抬起时间 50 - A按下时间 0 = 50
       - B按下 → ["0x05", "0x000064", "0x000032"]
         start=100，因为 B按下时间 100 - 上一个输出事件 A按下时间 0 = 100
         duration=50，因为 B抬起时间 150 - B按下时间 100 = 50
       - C按下 → ["0x06", "0x000064", "0x000032"]
         start=100，因为 C按下时间 200 - 上一个输出事件 B按下时间 100 = 100
         duration=50，因为 C抬起时间 250 - C按下时间 200 = 50

     以 A按下 → B按下 → A抬起 → B抬起 为例:
       keyOrder: ["0x1104", "0x1105", "0x1004", "0x1005"]
       pressTime: [30, 40, 50] 或 [30, 40, 50, 50]

     第1步：先还原 UI 事件流的绝对时间线
       - eventStartTimes[0] = 0      → A按下
       - eventStartTimes[1] = 30     → B按下
       - eventStartTimes[2] = 70     → A抬起
       - eventStartTimes[3] = 120    → B抬起
       - 如果有最后那个哨兵 50，它不参与这一步，只是给 UI 保留末尾输入位

     第2步：只输出设备需要的“按下事件三元组”
       - A按下 → ["0x04", "0x000000", "0x000046"]
         start=0，因为它是第一个输出事件
         duration=70，因为 A抬起时间 70 - A按下时间 0 = 70
       - B按下 → ["0x05", "0x00001e", "0x00005a"]
         start=30，因为 B按下时间 30 - 上一个输出事件 A按下时间 0 = 30
         duration=90，因为 B抬起时间 120 - B按下时间 30 = 90

     关键规则:
       1. 只对 0x11xx 按下事件输出三元组，0x10xx 松开事件只用于算 duration
       2. relativeStart = 当前按下绝对时间 - 上一个已输出事件的绝对时间
       3. duration = 对应松开绝对时间 - 当前按下绝对时间
       4. 坐标事件 0xff 没有松开事件，第三项直接保存 xy 数据
     5. 未找到对应松开事件时 duration 使用 0xea60，表示按下不抬起
  */
  setMacroData({ commit }, macroData) {
    const normalizedMacroData = (macroData || []).map((item, index) =>
      normalizeAM35MacroEntry(item, index)
    );
    commit("SET_MACRO_DATA", normalizedMacroData);
    console.log("macroData", normalizedMacroData);
    const mouseMacroData = normalizedMacroData.map((i, index) => {
      const macro = {
        index: i.index || index,
        macro_name: i.macro_name,
        macroValue: {
          keyOrder: [],
        },
        keyID: i.keyID,
        toKeyID: i.toKeyID,
        repeat_mode: i.repeat_mode,
        repeat_times: i.repeat_times,
        setAM35MacroLoopInterval: buildAM35MacroLoopIntervalPayload(
          i.setAM35MacroLoopInterval,
          getAM35MacroLoopIntervalValue(i)
        ),
      };
      const keyOrder = i.macroValue.keyOrder;
      const pressTime = Array.isArray(i.macroValue.pressTime)
        ? i.macroValue.pressTime
        : [];
      const newKeyOrder = [];
      const eventStartTimes = [];
      let currentAbsoluteTime = 0;
      let lastOutputStartTime = 0;
      let hasOutputEvent = false;

      for (let eventIndex = 0; eventIndex < keyOrder.length; eventIndex++) {
        eventStartTimes[eventIndex] = currentAbsoluteTime;
        if (eventIndex < keyOrder.length - 1) {
          currentAbsoluteTime += pressTime[eventIndex] || 0;
        }
      }
      
      for (let index = 0; index < keyOrder.length; index++) {
        const item = keyOrder[index];
        const eventStartTime = eventStartTimes[index] || 0;
        const relativeStartTime = hasOutputEvent
          ? eventStartTime - lastOutputStartTime
          : 0;
        
        if (isAM35MacroCoordinateEvent(item)) {
          // 鼠标坐标事件（0xff）
          newKeyOrder.push("0xff");
          newKeyOrder.push(`0x${relativeStartTime.toString(16).padStart(6, "0")}`);
          newKeyOrder.push(
            `0x${item.x.toString(16).padStart(3, "0")}${item.y
              .toString(16)
              .padStart(3, "0")}`
          );
          lastOutputStartTime = eventStartTime;
          hasOutputEvent = true;
        } else if (item.includes("0x11")) {
          // 按键按下事件(0x11xx)
          const keycode = item.replace("0x11", "0x"); // 提取纯键码
          const upKey = item.replace("0x11", "0x10"); // 构造对应的松开键码
          
          // 向前查找对应的松开事件，持续时间按绝对时间差计算
          let duration = AM35_MACRO_PRESS_ONLY_DURATION;
          for (let j = index + 1; j < keyOrder.length; j++) {
            if (keyOrder[j] && keyOrder[j] === upKey) {
              duration = eventStartTimes[j] - eventStartTime;
              if (duration === AM35_MACRO_PRESS_ONLY_DURATION) {
                duration = AM35_MACRO_PRESS_ONLY_DURATION - 1;
              }
              break;
            }
          }
          
          // 输出三元组: [按键码, 相对开始时间, 持续时间]
          newKeyOrder.push(keycode);
          newKeyOrder.push(`0x${relativeStartTime.toString(16).padStart(6, "0")}`);
          newKeyOrder.push(`0x${duration.toString(16).padStart(6, "0")}`);
          lastOutputStartTime = eventStartTime;
          hasOutputEvent = true;
        }
        // 松开事件(0x10xx)跳过,不输出到设备数据
      }

      macro.macroValue.keyOrder = newKeyOrder;
      return macro;
    });
    commit("SET_MOUSE_MACRO_DATA", mouseMacroData);
  },

  /* 设备数据 → UI数据转换 (setMacroData的逆过程)
     输入格式 (设备): 
       keyOrder: ["0x04", "0x000000", "0x000032", "0x05", "0x000064", "0x000032", "0x06", "0x000064", "0x000032"]
     
     输出格式 (UI):
       keyOrder: ["0x1104", "0x1004", "0x1105", "0x1005", "0x1106", "0x1006"]  
       pressTime: [50, 50, 50, 50, 50]

     解析过程:
       - A: relativeStart=0, absoluteTime=0, duration=50
         → 按下(0x1104, 0) 松开(0x1004, 50)
       - B: relativeStart=100, absoluteTime=0+100=100, duration=50
         → 按下(0x1105, 100) 松开(0x1005, 150)  
       - C: relativeStart=100, absoluteTime=100+100=200, duration=50
         → 按下(0x1106, 200) 松开(0x1006, 250)

     排序后: [0x1104(0), 0x1004(50), 0x1105(100), 0x1005(150), 0x1106(200), 0x1006(250)]

     计算 pressTime (相邻事件时间间隔):
       - [0]→[1]: 50-0=50 ✅
       - [1]→[2]: 100-50=50 ✅ 
       - [2]→[3]: 150-100=50 ✅
       - [3]→[4]: 200-150=50 ✅
       - [4]→[5]: 250-200=50 ✅

     最后一位为什么还会多一个 50:
       - 最后一个事件后面已经没有“下一个事件”了，本来协议语义上不再需要间隔
       - 这里额外补一个 50，当作 AM35 UI 的末尾哨兵位
       - 所以 setMouseMacroData 回填出来的结果，通常会满足：
         pressTime.length === keyOrder.length
  */
  setMouseMacroData({ commit }, mouseMacroData) {
    commit("SET_MOUSE_MACRO_DATA", mouseMacroData);
    let macroData = [];
    if (mouseMacroData && mouseMacroData.length > 0) {
      macroData = mouseMacroData.map((i, index) => {
        const macro = {
          index: i.index || index,
          macro_name: i.macro_name,
          macroValue: {
            keyOrder: [],
            pressTime: [],
          },
          keyID: i.keyID,
          toKeyID: i.toKeyID,
          repeat_mode: i.repeat_mode,
          repeat_times: i.repeat_times,
          setAM35MacroLoopInterval: buildAM35MacroLoopIntervalPayload(
            i.setAM35MacroLoopInterval
          ),
        };
        const keyOrder = i.macroValue.keyOrder;
        const loopInterval = buildAM35MacroLoopIntervalPayload(
          i.setAM35MacroLoopInterval
        );
        const newKeyOrder = [];
        let absoluteTime = 0; // 累加相对时间转为绝对时间
        
        for (let j = 0; j < keyOrder.length; j += 3) {
          const keys = keyOrder.slice(j, j + 3);
          const relativeStartTime = parseInt(keys[1], 16); // 相对于上一个按键的时间
          const duration = parseInt(keys[2], 16);
          
          // 将相对时间累加为绝对时间
          absoluteTime += relativeStartTime;
          
          if (keys[0] == "0xff") {
            const x = parseInt(`0x${keys[2].slice(2, 5)}`, 16);
            const y = parseInt(`0x${keys[2].slice(5)}`, 16);
            newKeyOrder.push({
              x,
              y,
              start_time: absoluteTime,
            });
          } else {
            // 按下事件
            newKeyOrder.push({
              key: keys[0].replace("0x", "0x11"),
              start_time: absoluteTime,
            });
            // 只有协议给了真实时长时才补回松开事件；0xea60 表示按下不抬起
            if (duration !== AM35_MACRO_PRESS_ONLY_DURATION) {
              newKeyOrder.push({
                key: keys[0].replace("0x", "0x10"),
                start_time: absoluteTime + duration,
              });
            }
          }
        }
        
        newKeyOrder.sort((a, b) => a.start_time - b.start_time);
        newKeyOrder.map((item, index) => {
          if (item.key) {
            macro.macroValue.keyOrder.push(item.key);
          } else {
            macro.macroValue.keyOrder.push({
              x: item.x,
              y: item.y,
            });
          }
          if (newKeyOrder[index + 1]) {
            macro.macroValue.pressTime.push(
              newKeyOrder[index + 1].start_time - item.start_time
            );
          } else {
            macro.macroValue.pressTime.push(loopInterval.interval);
          }
        });
        const clickMacroMeta = inferAM35ClickMacroFromDevicePayload(
          i.macroValue?.keyOrder
        );
        if (clickMacroMeta) {
          macro.macro_type = clickMacroMeta.macro_type;
          macro.repeat_mode = i.repeat_mode === 2 ? 2 : i.repeat_mode === 1 ? 1 : 0;
          macro.repeat_times =
            macro.repeat_mode === 0
              ? normalizeAM35BindingRepeatCount(i.repeat_times, 1)
              : 0;
          macro.click_key = clickMacroMeta.click_key;
          macro.click_interval = clickMacroMeta.click_interval;
          macro.click_repeat_times = clickMacroMeta.click_repeat_times;
        } else {
          macro.macro_type = "normal";
        }
        return macro;
      });
    }
    commit("SET_MACRO_DATA", macroData);
  },
  setParam({ commit }, param) {
    commit("SET_PARAM", param);
    // 同步更新mouseID到mouseArgs
    if (param && param.mouseID) {
      commit("SET_MOUSE_ID", param.mouseID);
    }
  },
  setMouseID({ commit }, mouseID) {
    commit("SET_MOUSE_ID", mouseID);
  },
  setLight({ commit }, light) {
    commit("SET_LIGHT_DATA", light);
  },
};

const mutations = {
  // 新增设置整个mouseArgs对象的mutation
  SET_MOUSE_ARGS: (state, mouseArgs) => {
    // 使用对象合并更新mouseArgs，保留未在新对象中指定的现有值
    const nextMouseArgs = { ...state.mouseArgs, ...mouseArgs };
    if (mouseArgs?.currentProfile !== undefined) {
      nextMouseArgs.currentProfile = clampAM35CurrentProfile(
        mouseArgs.currentProfile,
        nextMouseArgs.currentProfile
      );
    } else if (mouseArgs?.CurrentProfile !== undefined) {
      nextMouseArgs.currentProfile = normalizeLegacyAM35CurrentProfile(
        mouseArgs.CurrentProfile,
        nextMouseArgs.currentProfile
      );
    }

    state.mouseArgs = nextMouseArgs;
  },
  SET_DPI: (state, DPI) => {
    state.mouseArgs.currentID = DPI.currentID;
    state.mouseArgs.xDPI = DPI.xDPI;
    state.mouseArgs.yDPI = DPI.yDPI;
    state.mouseArgs.DPIcolor = DPI.DPIcolor;
    state.mouseArgs.DPIsyn = DPI.DPIsyn;
    // 只有当DPIStages在参数中时才更新
    if (DPI.DPIStages !== undefined) {
      state.mouseArgs.DPIStages = DPI.DPIStages;
    }
    // 只有当getDPIButton在参数中时才更新
    if (DPI.getDPIButton !== undefined) {
      state.mouseArgs.getDPIButton = DPI.getDPIButton;
    }
  },
  SET_DEBOUNCE: (state, Debounce) => {
    // 新版：仅接受对象，分别设置三路
    if (Debounce && typeof Debounce === "object") {
      const { wireDebounce, rf24gDebounce, btDebounce } = Debounce;
      if (wireDebounce != null) state.mouseArgs.wireDebounce = Number(wireDebounce);
      if (rf24gDebounce != null) state.mouseArgs.rf24gDebounce = Number(rf24gDebounce);
      if (btDebounce != null) state.mouseArgs.btDebounce = Number(btDebounce);
    }
  },
  SET_REPORT_RATE: (state, reportRate) => {
    state.mouseArgs.reportRate = reportRate;
  },
  SET_LOD: (state, LOD) => {
    state.mouseArgs.LOD = LOD;
  },
  SET_MOTION_SYNC: (state, MotionSync) => {
    state.mouseArgs.MotionSync = MotionSync;
  },
  SET_ANGLE_SNAPPING: (state, AngleSnapping) => {
    state.mouseArgs.AngleSnapping = AngleSnapping;
  },
  SET_RIPPLE_CTRL: (state, RippleCtrl) => {
    state.mouseArgs.RippleCtrl = RippleCtrl;
  },
  SET_AM35FP_STATUS: (state, AM35FPStatus) => {
    state.mouseArgs.AM35FPStatus = AM35FPStatus;
  },
  SET_AM35_ROTATE_ANGLE: (state, rotateAngle) => {
    if (rotateAngle && typeof rotateAngle === "object") {
      const { AM35rotateAngleSwitch, setAM35rotateAngle } = rotateAngle;
      if (AM35rotateAngleSwitch != null) {
        state.mouseArgs.AM35rotateAngleSwitch = Number(AM35rotateAngleSwitch);
      }
      if (setAM35rotateAngle != null) {
        state.mouseArgs.setAM35rotateAngle = Number(setAM35rotateAngle);
      }
    }
  },
  SET_AM35_PROFILE_KEY_SWITCH: (state, profileKeySwitch) => {
    if (profileKeySwitch && typeof profileKeySwitch === "object") {
      const { AM35ProfileKeySwitch } = profileKeySwitch;
      if (AM35ProfileKeySwitch != null) {
        state.mouseArgs.AM35ProfileKeySwitch = Number(AM35ProfileKeySwitch);
      }
    }
  },
  SET_SLEEP_TIME_24G(state, sleepTime24G) {
    state.mouseArgs.sleepTime24G = sleepTime24G;
  },
  SET_SLEEP_TIME_BT(state, sleepTimeBT) {
    state.mouseArgs.sleepTimeBT = sleepTimeBT;
  },

  SET_LIGHT_DATA: (state, lightData) => {
    // 更新 lightData 对象中的属性
    Object.keys(lightData).forEach(key => {
      if (state.lightData.hasOwnProperty(key)) {
        state.lightData[key] = lightData[key];
      }
    });
  },

  SET_VERSION: (state, { version, isLatestVersion }) => {
    state.version = version;
    state.isLatestVersion = isLatestVersion;
  },
  SET_PROFILE: (state, profile) => {
    state.profile = profile;
  },
  SET_KEY_MATRIX: (state, keyMatrix) => {
    state.keyMatrix = keyMatrix;
  },
  SET_MACRO_DATA: (state, macroData) => {
    state.macroData = macroData;
  },
  SET_MOUSE_MACRO_DATA: (state, mouseMacroData) => {
    state.mouseMacroData = mouseMacroData;
  },
  SET_PARAM: (state, param) => {
    state.param = param;
  },
  SET_MOUSE_ID: (state, mouseID) => {
    state.mouseArgs.mouseID = mouseID;
    // 同步更新到param对象以保持向后兼容
    if (!state.param) state.param = {};
    state.param.mouseID = mouseID;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
